import React from 'react';
import { Box, Typography, Select, MenuItem, FormControl } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#4A5568',
  },
  select: {
    '& .MuiSelect-select': {
      fontSize: 12,
      fontWeight: 700,
      padding: theme.spacing(0.5, 3, 0.5, 1),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
}));

const MIN_DATE = new Date(2025, 11, 1); // Dec 2025

const formatDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const generateMonthOptions = (): { value: string; label: string }[] => {
  const options: { value: string; label: string }[] = [];
  const now = new Date();

  for (let i = 0; i < 24; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    if (date < MIN_DATE) break;
    const value = formatDateString(date);
    const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    options.push({ value, label });
  }

  return options;
};

export interface DateRange {
  start: string;
}

interface DateRangePickerProps {
  dateRange: DateRange;
  onChange: (dateRange: DateRange) => void;
}

export const getDefaultDateRange = (): DateRange => {
  const start = new Date();
  start.setMonth(start.getMonth() - 6);
  start.setDate(1);

  const effectiveStart = start < MIN_DATE ? MIN_DATE : start;

  return {
    start: formatDateString(effectiveStart),
  };
};

export const parseDateRangeFromParams = (searchParams: URLSearchParams): DateRange => {
  const startParam = searchParams.get('startDate');

  if (startParam) {
    const paramDate = new Date(startParam);
    if (paramDate < MIN_DATE) {
      return { start: formatDateString(MIN_DATE) };
    }
    return { start: startParam };
  }

  return getDefaultDateRange();
};

export const updateDateRangeParams = (dateRange: DateRange): void => {
  const url = new URL(window.location.href);
  url.searchParams.set('startDate', dateRange.start);
  url.searchParams.delete('endDate');
  window.history.replaceState({}, '', url.toString());
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateRange,
  onChange,
}) => {
  const classes = useStyles();
  const monthOptions = generateMonthOptions();

  const handleChange = (event: { target: { value: string } }) => {
    const newRange = { start: event.target.value };
    onChange(newRange);
    updateDateRangeParams(newRange);
  };

  // Find the matching option, or find the closest month if exact match isn't found
  const findMatchingOption = () => {
    const exactMatch = monthOptions.find((opt) => opt.value === dateRange.start);
    if (exactMatch) return exactMatch.value;

    // If no exact match, find the option for the same month (ignore day differences)
    const startMonth = dateRange.start.substring(0, 7); // "YYYY-MM"
    const monthMatch = monthOptions.find((opt) => opt.value.substring(0, 7) === startMonth);
    if (monthMatch) return monthMatch.value;

    // Fall back to 6 months ago (index 6) or last available option
    const sixMonthsAgoIndex = Math.min(6, monthOptions.length - 1);
    return monthOptions[sixMonthsAgoIndex]?.value || dateRange.start;
  };

  const currentValue = findMatchingOption();

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>Since</Typography>
      <FormControl size="small">
        <Select
          value={currentValue}
          onChange={handleChange}
          className={classes.select}
          variant="outlined"
        >
          {monthOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default DateRangePicker;
